import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, QrCode, Share2, CheckCircle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { DepartmentSelect } from '../../components/ui/DepartmentSelect';
import { useRegisterEmployees, useReissuePairing } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { LIFF_ID } from '../../lib/liff';
import { useAuthStore } from '../../lib/store';

/** ลิงก์ LIFF หน้าผูกบัญชีพนักงาน (จับคู่ด้วยรหัส). อ่าน LIFF_ID สดตอนเรียก
 *  (ไม่ snapshot ตอน module โหลด) เพราะตอนนี้ LIFF_ID มาจาก /public/line-config
 *  ที่ resolve หลัง module นี้ import แล้ว. */
function getPairLink(): string {
  return LIFF_ID ? `https://liff.line.me/${LIFF_ID}/pair` : '';
}

/** ข้อความเชิญพร้อมส่ง: ลิงก์ + 2 รหัส + ขั้นตอน. */
function buildPairMessage(a: { name: string; tenantCode: string; pairingCode: string; expiresAt: string }): string {
  const link = getPairLink() || 'เปิดแอป PayPilot ในเมนู LINE ของบริษัท';
  const expires = new Date(a.expiresAt).toLocaleString('th-TH');
  return `สวัสดีคุณ ${a.name} 🎉

คุณได้รับเชิญเข้าใช้ระบบเงินเดือน (PayPilot)

📱 วิธีเชื่อมบัญชี:
1) เปิดลิงก์นี้ในแอป LINE
   ${link}
2) เลือก "ฉันเป็นพนักงาน"
3) กรอก 2 รหัสนี้:
   🏢 รหัสบริษัท:  ${a.tenantCode}
   🔑 รหัสจับคู่:  ${a.pairingCode}
4) ถ่ายเซลฟี่ยืนยันตัวตน แล้วกรอกข้อมูลส่วนตัว

⏰ รหัสหมดอายุ: ${expires}`;
}

/**
 * เพิ่มพนักงานทีละคน (minimal) — ชื่อ-สกุล (บังคับ) + เลขบัตร/แผนก/ตำแหน่ง (ไม่บังคับ).
 *  - ใส่เลขบัตร  → ได้ลิงก์ให้พนักงานยืนยันเองทาง LINE (claim ด้วยเลขบัตร)
 *  - ไม่ใส่เลขบัตร → ระบบออก "รหัสจับคู่" ให้ส่งพนักงานไปผูก LINE แทน
 * ที่เหลือ (ข้อมูลส่วนตัว) พนักงานกรอกเองทาง LINE · เงินเดือน HR ตั้งในหน้าแก้ไข.
 */
type AddResult =
  | { mode: 'claim'; tenant_code: string; name: string }
  | { mode: 'pair'; tenant_code: string; pairing_code: string; expires_at: string; name: string };

/** ลิงก์ LIFF หน้า claim (ยืนยันด้วยเลขบัตร) สำหรับ tenant นี้. */
const claimLinkFor = (code: string) =>
  code && LIFF_ID ? `https://liff.line.me/${LIFF_ID}/claim?tenant=${code}` : '';

/** ข้อความเชิญ (claim path) พร้อมส่ง: ลิงก์ + ขั้นตอน. */
function buildClaimMessage(a: { name: string; tenantCode: string }): string {
  const link = claimLinkFor(a.tenantCode) || 'เปิดแอป PayPilot ในเมนู LINE ของบริษัท';
  return `สวัสดีคุณ ${a.name} 🎉

คุณได้รับเชิญเข้าใช้ระบบเงินเดือน (PayPilot)

📱 วิธีเชื่อมบัญชี:
1) เปิดลิงก์นี้ในแอป LINE
   ${link}
2) กรอกเลขบัตรประชาชนของคุณ
3) ยืนยันชื่อที่ระบบแสดง
4) เชื่อมบัญชี แล้วกรอกข้อมูลส่วนตัว`;
}

export function OwnerOnboardContent() {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [nidRaw, setNidRaw] = useState('');
  const [department, setDepartment] = useState('');
  const [position, setPosition] = useState('');
  const [result, setResult] = useState<AddResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const register = useRegisterEmployees();
  const reissue = useReissuePairing();
  const toast = useToast();
  const profileCode = useAuthStore((s) => s.profile?.tenant_code ?? null);

  const nid = nidRaw.replace(/\D/g, '');
  const nidOk = nid.length === 0 || nid.length === 13;
  const valid = first.trim() !== '' && last.trim() !== '' && nidOk;
  const busy = register.isPending || reissue.isPending;

  const claimLink = claimLinkFor;
  const pairLink = getPairLink();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) {
      toast.error(nid.length > 0 && nid.length !== 13 ? 'เลขบัตรต้องมี 13 หลัก (หรือเว้นว่าง)' : 'กรอกชื่อ-สกุลให้ครบ');
      return;
    }
    try {
      const r = await register.mutateAsync([{
        first_name: first.trim(),
        last_name: last.trim(),
        national_id: nid || undefined,
        department: department.trim() || undefined,
        position: position.trim() || undefined,
      }]);
      const c = r.created?.[0];
      if (r.registered === 0 || !c) {
        toast.error(r.skipped?.[0]?.reason ?? 'เพิ่มไม่สำเร็จ (อาจซ้ำ)');
        return;
      }
      const tenant = r.tenant_code ?? profileCode ?? '';
      if (c.has_nid) {
        setResult({ mode: 'claim', tenant_code: tenant, name: `${first.trim()} ${last.trim()}`.trim() });
      } else {
        // ไม่มีเลขบัตร → ออกรหัสจับคู่ให้คนนี้
        const p = await reissue.mutateAsync({ emp_code: c.emp_code });
        setResult({ mode: 'pair', tenant_code: tenant, pairing_code: p.pairing_code, expires_at: p.expires_at, name: `${first.trim()} ${last.trim()}`.trim() });
      }
      toast.success('เพิ่มพนักงานแล้ว');
      setFirst(''); setLast(''); setNidRaw(''); setDepartment(''); setPosition('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'เพิ่มไม่สำเร็จ');
    }
  }

  function copy(text: string, key: string) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  }

  async function shareOrCopy(msg: string) {
    const liff = (window as unknown as { liff?: { isApiAvailable: (s: string) => boolean; shareTargetPicker: (m: unknown[]) => Promise<unknown> } }).liff;
    if (liff?.isApiAvailable('shareTargetPicker')) {
      try { await liff.shareTargetPicker([{ type: 'text', text: msg }]); return; } catch { /* fall back to copy */ }
    }
    copy(msg, 'msg');
    toast.success('คัดลอกข้อความเชิญแล้ว — เปิด LINE แล้ววางส่งให้พนักงาน');
  }

  return (
    <div className="space-y-4">
      {result?.mode === 'claim' && (
        <div className="card p-4 bg-teal-50/40 space-y-3">
          <p className="font-semibold text-gray-900 inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#2f7d52]" /> เพิ่มพนักงานแล้ว — ส่งลิงก์ให้พนักงานยืนยันตัวตนทาง LINE</p>
          {claimLink(result.tenant_code) ? (
            <>
              <p className="text-sm text-gray-700">พนักงานเปิดลิงก์ในไลน์ → กรอกเลขบัตร → ยืนยันชื่อ → เชื่อมบัญชี + กรอกข้อมูลตัวเอง:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border rounded-lg px-2 py-1.5 break-all">{claimLink(result.tenant_code)}</code>
                <button onClick={() => copy(claimLink(result.tenant_code), 'link')} className="btn-secondary shrink-0 inline-flex items-center gap-1">
                  {copied === 'link' ? <Check className="w-4 h-4 text-teal" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500"><QrCode className="w-4 h-4" /> หรือสแกน QR:</div>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(claimLink(result.tenant_code))}`}
                alt="QR ลงทะเบียน" width={160} height={160} className="rounded-lg border bg-white p-1 mx-auto"
              />
              <ol className="text-xs text-gray-600 list-decimal list-inside space-y-0.5">
                <li>เปิดลิงก์/สแกน QR ในแอป LINE</li>
                <li>กรอกเลขบัตรประชาชน</li>
                <li>ยืนยันชื่อที่ระบบแสดง</li>
                <li>เชื่อมบัญชี แล้วกรอกข้อมูลส่วนตัว</li>
              </ol>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => shareOrCopy(buildClaimMessage({ name: result.name, tenantCode: result.tenant_code }))} className="btn-primary">
                  <Share2 className="w-4 h-4" /> ส่งทาง LINE
                </button>
                <button onClick={() => copy(buildClaimMessage({ name: result.name, tenantCode: result.tenant_code }), 'msg')} className="btn-secondary">
                  {copied === 'msg' ? <><Check className="w-4 h-4" /> คัดลอกแล้ว</> : <><Copy className="w-4 h-4" /> คัดลอกข้อความ</>}
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-amber-600">ยังสร้างลิงก์อัตโนมัติไม่ได้ (ไม่พบรหัสบริษัท) — ดูรหัสบริษัทที่หน้าตั้งค่า</p>
          )}
          <button onClick={() => setResult(null)} className="btn-secondary w-full text-sm">เพิ่มพนักงานคนต่อไป</button>
        </div>
      )}

      {result?.mode === 'pair' && (
        <div className="card p-4 bg-green-50 border-green-200 space-y-3">
          <p className="font-semibold text-gray-900 inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#2f7d52]" /> เพิ่มพนักงานแล้ว — ส่ง 2 รหัสนี้ให้พนักงานผูกบัญชีทาง LINE</p>
          <div className="bg-white rounded-xl border-2 border-green-400 p-4 space-y-3">
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">🏢 รหัสบริษัท</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-mono font-bold tracking-widest text-brand-700">{result.tenant_code || '—'}</div>
                {result.tenant_code && (
                  <button onClick={() => copy(result.tenant_code, 'tenant')} className="btn-secondary text-xs">
                    {copied === 'tenant' ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>
            <div className="border-t border-gray-100" />
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">🔑 รหัสจับคู่</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-mono font-bold tracking-widest text-green-700">{result.pairing_code}</div>
                <button onClick={() => copy(result.pairing_code, 'pair')} className="btn-secondary text-xs">
                  {copied === 'pair' ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">⏰ หมดอายุ: {new Date(result.expires_at).toLocaleString('th-TH')}</div>
            </div>
          </div>
          {/* ลิงก์ + ขั้นตอน ให้ส่งพนักงาน */}
          {pairLink && (
            <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
              <div className="text-xs text-gray-500">ลิงก์ผูกบัญชี (เปิดในไลน์)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs break-all text-gray-800">{pairLink}</code>
                <button onClick={() => copy(pairLink, 'link')} className="btn-secondary text-xs shrink-0">
                  {copied === 'link' ? <Check className="w-3.5 h-3.5 text-teal" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          )}
          <ol className="text-xs text-gray-600 list-decimal list-inside space-y-0.5">
            <li>เปิดลิงก์ด้านบนในแอป LINE</li>
            <li>เลือก "ฉันเป็นพนักงาน"</li>
            <li>กรอกรหัสบริษัท + รหัสจับคู่</li>
            <li>ถ่ายเซลฟี่ยืนยันตัวตน แล้วกรอกข้อมูลส่วนตัว</li>
          </ol>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => shareOrCopy(buildPairMessage({ name: result.name, tenantCode: result.tenant_code, pairingCode: result.pairing_code, expiresAt: result.expires_at }))} className="btn-primary">
              <Share2 className="w-4 h-4" /> ส่งทาง LINE
            </button>
            <button
              onClick={() => copy(buildPairMessage({ name: result.name, tenantCode: result.tenant_code, pairingCode: result.pairing_code, expiresAt: result.expires_at }), 'msg')}
              className="btn-secondary"
            >
              {copied === 'msg' ? <><Check className="w-4 h-4" /> คัดลอกแล้ว</> : <><Copy className="w-4 h-4" /> คัดลอกข้อความ</>}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center">ปุ่มซ้ายเปิด share ของ LINE · ปุ่มขวาคัดลอกข้อความเชิญทั้งหมด (ลิงก์+รหัส+ขั้นตอน)</p>
          <button onClick={() => setResult(null)} className="btn-secondary w-full text-sm">เพิ่มพนักงานคนต่อไป</button>
        </div>
      )}

      <form onSubmit={submit} className="card p-4 space-y-3">
        <p className="text-sm text-gray-500">
          กรอกแค่ <b>ชื่อ-สกุล</b> ก็พอ · ใส่ <b>เลขบัตร</b> ด้วยจะได้ลิงก์ให้พนักงานยืนยันเองทาง LINE (ไม่ใส่ = ระบบออกรหัสจับคู่ให้). แผนก/ตำแหน่ง/เงินเดือน เติมทีหลังได้
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">ชื่อ *</label>
            <input className="input" value={first} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div>
            <label className="label">นามสกุล *</label>
            <input className="input" value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">เลขบัตรประชาชน (ไม่บังคับ)</label>
          <input
            className="input"
            inputMode="numeric"
            value={nidRaw}
            onChange={(e) => setNidRaw(e.target.value)}
            placeholder="13 หลัก — เว้นว่างได้ (จะออกรหัสจับคู่ให้แทน)"
          />
          {nidRaw !== '' && nid.length !== 13 && (
            <p className="text-xs text-amber-600 mt-1">เลขบัตรต้องมี 13 หลัก ({nid.length}/13) — หรือเว้นว่าง</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">แผนก</label>
            <DepartmentSelect value={department} onChange={setDepartment} />
          </div>
          <div>
            <label className="label">ตำแหน่ง</label>
            <input className="input" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="เว้นว่างได้" />
          </div>
        </div>
        <button type="submit" disabled={busy || !valid} className="btn-primary w-full">
          {busy ? 'กำลังเพิ่ม…' : 'เพิ่มพนักงาน'}
        </button>
      </form>

      <div className="text-xs text-gray-400 px-1 text-center">
        ต้องการเพิ่มทีละหลายคน?{' '}
        <Link to="/owner/register-employees" className="text-teal hover:underline">นำเข้าหลายคน (วางรายชื่อ + เลขบัตร)</Link>
      </div>
    </div>
  );
}

/** Standalone page kept for legacy /owner/onboard (now redirects via People hub). */
export function OwnerOnboard() {
  return (
    <AdminLayout title="เพิ่มพนักงานใหม่">
      <OwnerOnboardContent />
    </AdminLayout>
  );
}
